import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videoCategories, videos, Video } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = videoCategories.find((c) => c.slug === slug);

  const categoryVideos = useMemo(() => {
    if (!slug) return videos;
    if (slug === 'hot') return videos.filter((v) => v.isHot);
    return videos.filter((v) => v.category === slug);
  }, [slug]);

  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');
  const [sortedVideos, setSortedVideos] = useState<Video[]>([]);

  useEffect(() => {
    const sorted = [...categoryVideos];
    if (sortBy === 'hot') {
      sorted.sort((a, b) => parseFloat(b.views.replace('万', '')) - parseFloat(a.views.replace('万', '')));
    } else if (sortBy === 'new') {
      sorted.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
    } else {
      sorted.sort((a, b) => b.rating - a.rating);
    }
    setSortedVideos(sorted);
  }, [categoryVideos, sortBy]);

  const pageTitle = category ? `${category.name} - 蓝莓视频` : '全部分类 - 蓝莓视频';
  const pageDesc = category?.description || '浏览蓝莓视频全部精彩内容';

  const catJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://www.lanmeishipin.com/category/${slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(catJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1400px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '全部分类' },
              ]}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category?.name || '全部分类'}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {category?.description || '浏览蓝莓视频全部精彩内容'}
                </p>
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {(['hot', 'new', 'rating'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === s
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {s === 'hot' ? '最热' : s === 'new' ? '最新' : '评分'}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {videoCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-accent-100 text-accent-900 border border-accent-300'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-transparent'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {sortedVideos.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {sortedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:scale-[1.02] transition-all cursor-pointer border border-background-200/70"
                    data-product-shop
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 蓝莓视频高清在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2 flex items-center gap-1.5">
                        {video.isHot && (
                          <span className="px-2 py-0.5 bg-primary-500 text-white text-xs rounded-md font-medium">热门</span>
                        )}
                        {video.isNew && (
                          <span className="px-2 py-0.5 bg-accent-500 text-white text-xs rounded-md font-medium">新上</span>
                        )}
                      </div>
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
                          <i className="ri-play-fill text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h2 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-1 mb-1">
                        {video.title}
                      </h2>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          {video.rating}
                        </span>
                        <span>{video.views}次播放</span>
                        <span className="ml-auto px-1.5 py-0.5 bg-background-200 rounded text-foreground-600 text-[10px]">
                          {video.quality}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}